import { Alert } from '@repo/ui/components/Alert';
import { Button } from '@repo/ui/components/Button';
import { toast } from '@repo/ui/components/useToast';
import { cn } from '@repo/ui/utils';
import { useGetActiveAnnouncement } from '@src/api/useAnnouncements';
import { useGetUser, useUpdateUser } from '@src/api/useUser';
import { useEmbeddedAuth } from '@src/contexts/embedded-auth/useEmbeddedAuth';
import { SimpleEditor } from '@src/components/tiptap-templates/simple/simple-editor';
import { Loader2Icon, MegaphoneIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { BaseDialog, DialogClose, DialogFooter } from './BaseDialog';
import { menuExclusionRoutes } from './Navigation';

export function WhatsNewBanner() {
  const { isAuthenticated } = useEmbeddedAuth();
  const { data: user, isLoading: isLoadingUser } = useGetUser(isAuthenticated);
  const updateUser = useUpdateUser();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const isExcludedRoute = menuExclusionRoutes.some((path) =>
    location.pathname.startsWith(path)
  );

  const hasAcceptedTerms = !!user?.metadata?.termsAndConditionsAcceptedAt;
  const { data: announcement } = useGetActiveAnnouncement(
    isAuthenticated && hasAcceptedTerms
  );
  const isDismissed =
    !!announcement && user?.metadata?.seenAnnouncementId === announcement.id;

  const shouldShow =
    isAuthenticated &&
    !isLoadingUser &&
    hasAcceptedTerms &&
    !isExcludedRoute &&
    !!announcement &&
    !isDismissed;

  if (!shouldShow) {
    return null;
  }

  const dismiss = async () => {
    setIsOpen(false);
    try {
      await updateUser.mutateAsync({
        metadata: { seenAnnouncementId: announcement.id },
      });
    } catch (error) {
      toast({
        title: 'Error dismissing announcement',
        description:
          error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Alert
        variant="success"
        role="region"
        aria-labelledby="whats-new-banner-title"
        className={cn(
          'mb-4',
          'flex',
          'items-center',
          'gap-3',
          'py-3',
          'pl-4',
          'pr-2',
          '[&>svg]:static',
          '[&>svg]:translate-y-0',
          '[&>svg~*]:pl-0'
        )}
      >
        <MegaphoneIcon aria-hidden="true" className={cn('h-5', 'w-5')} />
        <p
          id="whats-new-banner-title"
          className={cn(
            'flex-1',
            'min-w-0',
            'truncate',
            'font-medium',
            'leading-normal',
            'text-foreground'
          )}
        >
          <span className={cn('sr-only')}>What&apos;s new: </span>
          {announcement.title}
        </p>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setIsOpen(true)}
          className={cn('shrink-0', '!px-3')}
        >
          Read more
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={dismiss}
          disabled={updateUser.isLoading}
          aria-label="Dismiss announcement"
          className={cn('shrink-0', 'h-9', 'w-9')}
        >
          {updateUser.isLoading ? (
            <Loader2Icon
              className={cn('h-4', 'w-4', 'animate-spin')}
              aria-hidden="true"
            />
          ) : (
            <XIcon className={cn('h-4', 'w-4')} aria-hidden="true" />
          )}
        </Button>
      </Alert>
      <span role="status" className={cn('sr-only')}>
        {updateUser.isLoading ? 'Dismissing announcement' : ''}
      </span>

      <BaseDialog
        title={announcement.title}
        open={isOpen}
        onOpenChange={setIsOpen}
        className={cn(
          'max-w-2xl',
          'max-h-[90vh]',
          'flex',
          'flex-col',
          'overflow-y-hidden'
        )}
      >
        <div className={cn('flex-1', 'min-h-0', 'overflow-y-auto')}>
          <SimpleEditor value={announcement.bodyHtml} readOnly />
        </div>
        <DialogFooter className={cn('shrink-0')}>
          <DialogClose asChild>
            <Button
              variant="primary"
              onClick={dismiss}
              disabled={updateUser.isLoading}
            >
              {updateUser.isLoading ? (
                <>
                  <Loader2Icon
                    className={cn('animate-spin', 'mr-2', 'h-4', 'w-4')}
                    aria-hidden="true"
                  />
                  Saving...
                </>
              ) : (
                'Got it'
              )}
            </Button>
          </DialogClose>
        </DialogFooter>
      </BaseDialog>
    </>
  );
}
